import {
  CANONICAL_CTR_LABEL,
  deriveCtrPercentFromClicks,
} from "./canonicalCtr";
import {
  aggregateAllMetricBases,
  aggregateAllCountRatePairs,
  aggregateQualifiedVideoViews,
  describeUnavailableBasis,
  reconcileCountWithRate,
  UNGOVERNED_UNAVAILABLE_LABEL,
} from "../platformAnalytics/metricBasis";
import { formatCostPerCompletedView } from "../platformAnalytics/costPerCompletedView";
import {
  resolveSpendEfficiencyContract,
  resolveSpendEfficiencyMetric,
} from "../platformAnalytics/spendEfficiencyContract";

const DEFAULT_AI_OPTIMIZATION_KPIS = {
  adSpend: "",
  impressions: "",
  clicks: "",
  ctr: "",
  cpc: "",
  engagements: "",
  engagementRate: "",
  videoViews: "",
  qualifiedVideoViews: "",
  costPerQualifiedVideoView: "",
  videoViewRate: "",
  videoCompletions: "",
  videoCompletionRate: "",
  conversions: "",
  conversionValue: "",
};

const AI_OPTIMIZATION_KPI_FIELDS = [
  ["adSpend", "TOTAL SPEND"],
  ["impressions", "Impressions"],
  ["clicks", "Clicks"],
  ["ctr", CANONICAL_CTR_LABEL],
  ["cpc", "CPC"],
  ["engagements", "Engagements"],
  ["engagementRate", "Engagement Rate"],
  /*
   * The video story reads top-down: the campaign-level qualified total first,
   * then the native measurements that produced it. Leading with "Video Views"
   * put YouTube's 28,135 at the head of a flight whose largest video outcome
   * was CTV's 139,122 completed views — a true number that summarised the
   * wrong thing, and the reason a scaling campaign looked like a collapsing
   * one. The native cards keep their own names underneath it; none of them
   * is renamed and none of them is blended.
   */
  ["qualifiedVideoViews", "Qualified Video Views"],
  /*
   * What the qualified total COST, directly beneath the total itself.
   *
   * This slot used to hold the raw Video Completions count, and the count was
   * the least useful of the three video figures on the strip: it restates a
   * number the qualified total already contains (CTV's completed views ARE
   * 170,556 of the 204,595 above it), while the campaign-level question a
   * reader actually brings to a video flight — what did a qualified view cost
   * — went unanswered anywhere on the page.
   *
   * The count is not lost. It remains on the platform detail rows, in the
   * qualified-video basis explainer, and as the numerator the completion rate
   * below states. What leaves is a top-strip card that spent a slot repeating
   * a component of its own neighbour.
   */
  ["costPerQualifiedVideoView", "Cost Per Qualified Video View"],
  ["videoViews", "Video Views"],
  ["videoViewRate", "Video View Rate"],
  ["videoCompletionRate", "Video Completion Rate"],
  ["conversions", "Conversions"],
  ["conversionValue", "Conversion Value"],
];

const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

/*
 * The card strip resolves its video SLOT the way every other governed surface
 * does: the qualified total where a platform carries a governed qualified
 * signal, the generic governed pair where none does.
 *
 * Leaving both on screen was the visible half of this defect. On a CTV+YouTube
 * flight the generic "Video Views 29,302" IS YouTube's TrueView count and the
 * generic "Video View Rate 25.06%" IS YouTube's VVR — the same two numbers the
 * QualifiedVideoSummary below already states, correctly attributed. Printed
 * again without their platform they read as the campaign's whole video
 * delivery, sitting directly beneath a qualified total more than five times
 * larger. Two cards, one measurement, and the unlabelled copy is the one that
 * misleads.
 *
 * A flight with no qualified ruling (Meta, TikTok) keeps the generic pair:
 * dropping it there would claim the campaign delivered no video at all.
 */
export const buildAiOptimizationKpiFields = (qualifiedVideo) => {
  const hasQualifiedBasis = Boolean(
    isPlainObject(qualifiedVideo) &&
      Array.isArray(qualifiedVideo.contributions) &&
      qualifiedVideo.contributions.length
  );
  if (!hasQualifiedBasis) {
    /*
     * CPQV leaves WITH the total it prices. It is qualified spend over
     * qualified views, so on a flight with no qualified ruling it has no
     * numerator and no denominator — not a missing value, an inapplicable
     * one. Leaving the card behind would ask a Meta or TikTok flight to
     * explain the absence of a cost for video it was never judged on.
     */
    return AI_OPTIMIZATION_KPI_FIELDS.filter(
      ([field]) => field !== "qualifiedVideoViews" && field !== "costPerQualifiedVideoView"
    );
  }
  return AI_OPTIMIZATION_KPI_FIELDS.filter(
    ([field]) => field !== "videoViews" && field !== "videoViewRate"
  );
};

const CANONICAL_REPORTING_CONTRACT = "canonical_reporting_v1";

const getCanonicalReportingContract = (value) => {
  if (!isPlainObject(value)) return "";
  return String(
    value.contract ||
      value.canonical_reporting_contract ||
      value.sourceContract?.contract ||
      value.source_contract?.contract ||
      ""
  )
    .trim()
    .toLowerCase();
};

const isCanonicalReportingPayload = (value) =>
  getCanonicalReportingContract(value) === CANONICAL_REPORTING_CONTRACT;

const isAggregateLike = (v) => {
  if (!isPlainObject(v)) return false;
  const hasTotals = isPlainObject(v.totals) || isPlainObject(v.total) || isPlainObject(v.summaryTotals);
  const hasBy = isPlainObject(v.byChannel) || isPlainObject(v.by_channel) || isPlainObject(v.channels);
  return !!(hasTotals && hasBy);
};

const normalizeAggregateLike = (v) => {
  if (!isAggregateLike(v)) return null;
  return {
    ...v,
    totals: v.totals || v.total || v.summaryTotals || {},
    byChannel: v.byChannel || v.by_channel || v.channels || {},
  };
};

const numberOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const buildAggregateFromCanonicalSummary = (summary) => {
  const totals = summary?.aggregateTotals || summary?.metrics;
  if (!isPlainObject(summary) || !isCanonicalReportingPayload(summary) || !isPlainObject(totals)) {
    return null;
  }
  const canonicalByChannel = isPlainObject(summary.byChannel) ? summary.byChannel : {};
  const canonicalPlatforms = isPlainObject(summary.platforms) ? summary.platforms : {};
  // A provider may deliver impressions while billing or another metric is unavailable.
  const normalizeCanonicalNumber = numberOrNull;

  return normalizeAggregateLike({
    totals: {
      impressions: normalizeCanonicalNumber(totals.impressions),
      clicks: normalizeCanonicalNumber(totals.clicks),
      engagements: normalizeCanonicalNumber(totals.engagements),
      qualified_video_views: numberOrNull(
        totals.qualifiedVideoViews ?? totals.qualified_video_views
      ),
      video_views: normalizeCanonicalNumber(totals.video_views ?? totals.videoViews),
      /*
       * `numberOrNull`, and no alias ladder.
       *
       * The canonical engine WITHHOLDS this count whenever CTV's 95%-threshold
       * completion would have to be added to YouTube's played-to-100% — two
       * definitions of "completed" whose sum is not a quantity. Withholding
       * means the key is ABSENT, and `numberOrZero` turned that absence into a
       * hard 0 which then printed beside CTV's governed 98.31% completion
       * rate: a campaign that completed nothing, 98% of the time.
       *
       * Every rung of the old ladder below the canonical key was a raw
       * provider field that survives the withholding, so the ladder reached
       * past the engine's refusal and rebuilt the very blend it declined.
       */
      video_completions: numberOrNull(totals.video_completions ?? totals.videoCompletions),
      completionRate:
        totals.videoCompletionRate ??
        totals.video_completion_rate ??
        totals.completionRate ??
        totals.completion_rate ??
        null,
      spend: normalizeCanonicalNumber(
        pickFirst(
          totals.totalSpend,
          totals.clientFacingSpend,
          totals.client_facing_spend,
          totals.billedSpend,
          totals.billed_spend,
          totals.clientBilledSpend,
          totals.client_billed_spend,
          totals.effectiveSpend,
          totals.effective_spend,
          totals.spendToDate,
          totals.spend_to_date,
          totals.reportSpend,
          totals.report_spend,
          totals.adSpend,
          totals.ad_spend,
          totals.spend
        )
      ),
      conversions: normalizeCanonicalNumber(totals.conversions),
      conversion_value: normalizeCanonicalNumber(totals.conversion_value ?? totals.conversionValue),
      video_started: normalizeCanonicalNumber(totals.video_started ?? totals.videoStarted),
      /* Same refusal as `video_completions` above — one withheld count, read
         under the payload's two casings and nothing else. */
      video_completed: numberOrNull(totals.video_completed ?? totals.videoCompleted),
      ticket_sales: normalizeCanonicalNumber(totals.ticket_sales ?? totals.ticketSales),
      users: normalizeCanonicalNumber(totals.users),
      new_users: normalizeCanonicalNumber(totals.new_users ?? totals.newUsers),
      sessions: normalizeCanonicalNumber(totals.sessions),
      engaged_sessions: normalizeCanonicalNumber(totals.engaged_sessions ?? totals.engagedSessions),
      event_count: normalizeCanonicalNumber(totals.event_count ?? totals.eventCount),
      average_session_duration: toNumber(
        totals.average_session_duration ??
          totals.avg_session_duration ??
          totals.averageSessionDuration
      ),
      /*
       * NOT an alias pair. `engagementRate` is the governed PAID rate, null
       * when the campaign's sources each count a different action as an
       * engagement; `engagement_rate` is GA4's SITE rate, engaged sessions
       * over sessions. Folding the second in as a fallback is how a session
       * rate came to print on a card labelled Engagement Rate beneath a paid
       * engagement count. The site rate keeps its own key.
       */
      engagementRate: pickFirst(totals.engagementRate, null),
      videoViewRate: pickFirst(totals.videoViewRate, totals.video_view_rate, null),
    },
    byChannel: canonicalByChannel,
    derivedMetrics: isPlainObject(summary.derivedMetrics) ? summary.derivedMetrics : null,
    /*
     * The governed qualified-video basis, carried through.
     *
     * This object is an explicit allowlist, and `qualifiedVideo` was simply
     * never added to it. The backend had published the basis and the totals
     * key since BE #221; the AI Optimizations card was declared, ordered and
     * styled — and read a field this builder dropped on the floor, so it
     * rendered an em dash on every campaign, forever. The payload was never
     * the problem; this line is.
     */
    qualifiedVideo: isPlainObject(summary.qualifiedVideo) ? summary.qualifiedVideo : null,
    spendEfficiency: isPlainObject(summary.spendEfficiency)
      ? summary.spendEfficiency
      : null,
    advertiserId: summary.advertiserId || null,
    campaignId: summary.campaignId || null,
    campaignScope: summary.campaignScope || null,
    scope: summary.scope || "active_campaigns",
    hasPerformanceData: summary.hasPerformanceData !== false,
    contract: summary.contract || null,
    platforms: canonicalPlatforms,
    platformScope:
      summary.platformScope ||
      (Object.keys(canonicalPlatforms).length
        ? { keys: Object.keys(canonicalPlatforms), count: Object.keys(canonicalPlatforms).length }
        : null),
    overallCtr: normalizeCanonicalNumber(totals.ctr),
    overallCpc: normalizeCanonicalNumber(totals.cpc),
    overallCpm: normalizeCanonicalNumber(totals.cpm),
    overallRoas: normalizeCanonicalNumber(totals.roas),
    canonicalMetrics: totals,
    sourceContract: {
      contract: summary.contract || null,
      version: summary.version || null,
      scope: summary.scope || "active_campaigns",
      spendBasis: summary.spendBasis || "client_billed",
      pricingBasis: summary.pricingBasis || "client_billed_where_configured",
      timeframe: summary.timeframe || null,
    },
    siteAnalytics: summary.siteAnalytics || null,
  });
};

const normalizeResolvedLike = (v) => {
  if (!isPlainObject(v)) return null;
  const totals = v.totals || v.total || v.summaryTotals || v.resolvedTotals || null;
  if (!isPlainObject(totals)) return null;
  return {
    ...v,
    totals,
    byChannel: v.byChannel || v.by_channel || v.channels || {},
  };
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === "string" && !v.trim()) continue;
    return v;
  }
  return null;
};

const toNumber = (v) => {
  if (v == null) return NaN;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (!s) return NaN;
  const cleaned = s.replace(/[\s]/g, "").replace(/\$/g, "").replace(/,/g, "").replace(/%/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const formatMoney = (v) => {
  if (v == null || v === "") return "$0.00";
  const num = typeof v === "number" ? v : toNumber(v);
  if (Number.isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(num)
    .replace(/^/, "$");
};

const formatNumber = (v) => {
  if (v == null || v === "") return "0";
  const num = typeof v === "number" ? v : toNumber(v);
  if (Number.isNaN(num)) return "0";
  return Number(num).toLocaleString("en-US");
};

const formatPercent = (v) => {
  const num = typeof v === "number" ? v : toNumber(v);
  if (v == null || Number.isNaN(num)) return "No Data";
  return `${Number(num).toFixed(2)}%`;
};

const computeRatePercent = (numerator, denominator) => {
  const num = toNumber(numerator ?? 0);
  const den = toNumber(denominator ?? 0);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return null;
  const pct = (num / den) * 100;
  return Number.isFinite(pct) ? pct : null;
};

const hasResolvedTotals = (resolved) =>
  isPlainObject(normalizeResolvedLike(resolved)?.totals) &&
  Object.keys(normalizeResolvedLike(resolved)?.totals || {}).length > 0;

const findAggregateDeep = (root) => {
  if (!isPlainObject(root)) return null;

  const direct = pickFirst(
    root.aggregate,
    root.aggregates,
    root.aggregateSnapshot,
    root.snapshot,
    root.result?.aggregate,
    root.result?.snapshot,
    root.data?.aggregate,
    root.data?.snapshot,
    root.run?.aggregate,
    root.run?.snapshot
  );
  const normalizedDirect = normalizeAggregateLike(direct);
  if (normalizedDirect) return normalizedDirect;

  for (const k of Object.keys(root)) {
    const v = root[k];
    const normalized = normalizeAggregateLike(v);
    if (normalized) return normalized;
    if (isPlainObject(v)) {
      for (const k2 of Object.keys(v)) {
        const v2 = v[k2];
        const normalizedNested = normalizeAggregateLike(v2);
        if (normalizedNested) return normalizedNested;
      }
    }
  }

  return null;
};

const findCanonicalAggregateDeep = (root) => {
  if (!isPlainObject(root)) return null;

  const summaryCandidates = [
    root.canonicalReporting,
    root.data?.canonicalReporting,
    root.result?.canonicalReporting,
    root.run?.canonicalReporting,
    root.canonicalMetrics?.summary,
    root.data?.canonicalMetrics?.summary,
    root.result?.canonicalMetrics?.summary,
    root.run?.canonicalMetrics?.summary,
    root.canonicalSummary,
    root.data?.canonicalSummary,
    root.result?.canonicalSummary,
    root.run?.canonicalSummary,
    root.performanceSummary,
    root,
  ];

  for (const candidate of summaryCandidates) {
    const aggregate = buildAggregateFromCanonicalSummary(candidate);
    if (aggregate) return aggregate;
  }

  const aggregateCandidates = [
    root.canonicalAggregate,
    root.data?.canonicalAggregate,
    root.result?.canonicalAggregate,
    root.run?.canonicalAggregate,
    root.aggregate,
    root.data?.aggregate,
    root.result?.aggregate,
    root.run?.aggregate,
  ];

  for (const candidate of aggregateCandidates) {
    const normalized = normalizeAggregateLike(candidate);
    if (normalized && isCanonicalReportingPayload(normalized)) {
      return normalized;
    }
  }

  return null;
};

const findResolvedDeep = (root) => {
  if (!isPlainObject(root)) return null;

  const direct = pickFirst(
    root.resolved,
    root.reportingTruth,
    root.resolvedReporting,
    root.result?.resolved,
    root.result?.reportingTruth,
    root.data?.resolved,
    root.data?.reportingTruth,
    root.run?.resolved,
    root.run?.reportingTruth
  );
  const normalizedDirect = normalizeResolvedLike(direct);
  if (normalizedDirect) return normalizedDirect;

  for (const k of Object.keys(root)) {
    const v = root[k];
    const normalized = normalizeResolvedLike(v);
    if (normalized) return normalized;
    if (isPlainObject(v)) {
      for (const k2 of Object.keys(v)) {
        const normalizedNested = normalizeResolvedLike(v[k2]);
        if (normalizedNested) return normalizedNested;
      }
    }
  }

  return null;
};

const computeAggregateMetrics = ({ aggregate = null, resolved = null } = {}) => {
  const normalizedResolved = normalizeResolvedLike(resolved);
  const normalizedAggregate = normalizeAggregateLike(aggregate);
  const t = hasResolvedTotals(normalizedResolved)
    ? normalizedResolved.totals
    : normalizedAggregate?.totals || null;
  if (!t) return null;

  /*
   * Governed blended rates. The canonical payload's derivedMetrics block is
   * the mathematical authority (it names value, numerator, denominator and
   * the eligible/excluded channels); when a payload predates it but carries
   * per-channel evidence, the SAME byte-pinned engine folds that evidence
   * locally, so the fallback cannot disagree with the authority. A governed
   * null is an answer — "these sources do not blend" — and must never fall
   * through to whole-pool division. Only a payload with no per-channel
   * evidence at all still derives from its own totals, where whole-scope and
   * single-scope coincide.
   */
  const governedAggregate = hasResolvedTotals(normalizedResolved)
    ? normalizedResolved
    : normalizedAggregate;
  const preserveMissingMetrics = governedAggregate?.sourceContract?.contract === "canonical_reporting_v1" || governedAggregate?.contract === "canonical_reporting_v1" || governedAggregate?.canonical_reporting_contract === "canonical_reporting_v1";
  const unavailableMetric = preserveMissingMetrics ? null : 0;
  const governedObservations = (() => {
    const byChannel = isPlainObject(governedAggregate?.byChannel)
      ? governedAggregate.byChannel
      : null;
    if (!byChannel || !Object.keys(byChannel).length) return null;
    return Object.entries(byChannel).map(([channel, entry]) => ({
      platformKey: channel,
      sourceKey: entry?.sourceLabel || entry?.metrics?.sourceLabel || channel,
      metrics: isPlainObject(entry?.metrics) ? entry.metrics : entry,
    }));
  })();
  const governedBases = (() => {
    if (isPlainObject(governedAggregate?.derivedMetrics)) {
      return { source: "canonical_derived_metrics", bases: governedAggregate.derivedMetrics };
    }
    if (governedObservations) {
      return {
        source: "shared_metric_basis",
        bases: aggregateAllMetricBases(governedObservations),
      };
    }
    return null;
  })();
  const governedRateValue = (metricKey) => {
    const basis = governedBases?.bases?.[metricKey];
    if (!isPlainObject(basis)) return undefined;
    return basis.value ?? null;
  };

  const impressions = toNumber(t.impressions ?? unavailableMetric);
  const clicks = toNumber(t.clicks ?? unavailableMetric);
  const videoViews = toNumber(t.video_views !== undefined ? t.video_views : t.videoViews ?? unavailableMetric);
  /*
   * No alias ladder, and no `?? 0` tail.
   *
   * The canonical engine WITHHOLDS this count whenever CTV's 95%-threshold
   * completion would have to be added to YouTube's played-to-100%: two
   * definitions of "completed" whose sum is not a quantity. Withholding means
   * the key is ABSENT — and every rung below the canonical one here
   * (`views100`, `q100`, `completedViews`, `completions`, …) is a raw provider
   * field that SURVIVES the withholding, so the ladder walked past the
   * engine's refusal and rebuilt the blend it had just declined. The `?? 0`
   * at the end then turned whatever was left into a hard zero.
   *
   * That is the "Video Completions 0" the campaign printed beside a governed
   * 98.31% completion rate.
   *
   * Withholding was the right refusal and it left the wrong result. The count
   * basis asks "may these totals be summed" and the completion RATE beside it
   * asks "who reported both halves" — different questions, and on this
   * campaign different answers: YouTube has a completion count and no starts,
   * so it breaks the sum but never entered the rate. The rate resolved on CTV
   * alone, at 98.38%, over a numerator of 165,084 that the strip then declared
   * unavailable. `governedPair` states both halves on the rate's own
   * population, so the pair is co-scoped rather than separately withheld.
   */
  const governedPairs = (() => {
    if (isPlainObject(governedAggregate?.countRatePairs)) return governedAggregate.countRatePairs;
    if (governedObservations) return aggregateAllCountRatePairs(governedObservations);
    return null;
  })();
  const governedPair = (pairKey) => {
    const pair = governedPairs?.[pairKey];
    return isPlainObject(pair) && pair.count !== null && pair.count !== undefined ? pair : null;
  };
  const completionsPair = governedPair("videoCompletions");

  /*
   * The engagement COUNT answers to the governed pair, like the video counts.
   *
   * The raw total is a cross-measurement sum: YouTube's ad interactions plus
   * StackAdapt's `landing_page_starts` plus a CTV zero-fill. Printed beside a
   * rate the engine scopes to one declared measurement, no division of the
   * count reaches the rate — 44,761 over anything is not 29.79%%. The pair
   * states the count on the rate's own population and names the rest.
   */
  const reportedEngagements = numberOrNull(t.engagements);
  const engagementsPair = governedPair("engagements");
  const engagements = engagementsPair ? engagementsPair.count : toNumber(t.engagements ?? unavailableMetric);
  const reportedCompletions = numberOrNull(
    t.video_completions !== undefined ? t.video_completions : t.videoCompletions
  );
  /*
   * The payload's own governed count still leads. It is the backend's answer
   * over the same observations, including a deliberate zero, and a locally
   * folded pair must not overrule a figure the canonical layer already stated.
   * The pair fills the gap the withholding left — which is the only case the
   * strip was getting wrong.
   */
  const videoCompletions =
    reportedCompletions !== null
      ? reportedCompletions
      : completionsPair
      ? completionsPair.count
      : null;
  /*
   * The qualified total: the payload's own governed value, else the same
   * registry folded over the same observations. Never assembled from the tiles
   * — which platforms carry a qualified signal is one ruling, made once.
   */
  const qualifiedVideoBasis = isPlainObject(governedAggregate?.qualifiedVideo)
    ? governedAggregate.qualifiedVideo
    : governedObservations
    ? aggregateQualifiedVideoViews(governedObservations)
    : null;
  const qualifiedVideoViews =
    numberOrNull(
      t.qualified_video_views !== undefined ? t.qualified_video_views : t.qualifiedVideoViews
    ) ??
    (qualifiedVideoBasis && qualifiedVideoBasis.value !== null
      ? numberOrNull(qualifiedVideoBasis.value)
      : null);
  /*
   * What the qualified total cost, on the SAME population that produced it.
   *
   * Never spend/qualifiedVideoViews computed here. The `spend` resolved below
   * is the whole campaign's — display dollars included — and dividing it by a
   * total only CTV and YouTube produced charges display for video it never
   * ran. On the live NSC flight that is $16,557.75/204,595 = $0.0809 against
   * the governed $14,452.40/204,595 = $0.0706, a 14.6% overstatement from one
   * scope decision. The engine folds numerator and denominator over the same
   * contributions by construction, which is the whole reason it exists.
   */
  const spendEfficiency = resolveSpendEfficiencyContract(governedAggregate);
  const costPerQualifiedVideoViewBasis = resolveSpendEfficiencyMetric(
    spendEfficiency,
    { metricKey: "cpqv" }
  );
  const costPerQualifiedVideoView = numberOrNull(
    costPerQualifiedVideoViewBasis?.value
  );
  const spend = toNumber(
    pickFirst(
      t.totalSpend,
      t.clientFacingSpend,
      t.client_facing_spend,
      t.billedSpend,
      t.billed_spend,
      t.clientBilledSpend,
      t.client_billed_spend,
      t.effectiveSpend,
      t.effective_spend,
      t.spendToDate,
      t.spend_to_date,
      t.reportSpend,
      t.report_spend,
      t.adSpend,
      t.ad_spend,
      t.spend,
      t.spend_usd,
      unavailableMetric
    )
  );
  const conversions = toNumber(t.conversions ?? unavailableMetric);

  /*
   * The explicit tier is the backend's own answer: a rate the canonical
   * payload carries on its totals (post-derivedMetrics, those ARE the
   * governed engine values, including deliberate nulls handled below) or a
   * rate a saved snapshot recorded. The governed tier folds per-channel
   * evidence through the shared engine. Whole-pool division survives only
   * for payloads with neither — where whole-scope and single-scope coincide.
   */
  const canonicalRates = isPlainObject(governedAggregate?.canonicalMetrics)
    ? governedAggregate.canonicalMetrics
    : {};
  const rateWithGovernance = (explicitValue, metricKey, wholePoolFallback) => {
    if (explicitValue != null) return explicitValue;
    const governedValue = governedRateValue(metricKey);
    if (governedValue !== undefined) return governedValue;
    return wholePoolFallback();
  };

  const ctr = rateWithGovernance(
    pickFirst(t.ctr, t.click_through_rate, canonicalRates.ctr),
    "ctr",
    () => deriveCtrPercentFromClicks({ clicks, impressions })
  );
  /*
   * `t.engagement_rate` is deliberately gone from this ladder.
   *
   * It is not another spelling of `engagementRate`. It is GA4's SITE figure —
   * engaged sessions over sessions — folded onto the same totals object by the
   * site-analytics pass, while `engagementRate` is the governed PAID rate over
   * the impressions of the platforms that reported engagements. On Never Stop
   * Chasing the paid rate is withheld (CTV, Display and YouTube each count a
   * different action as an engagement, so there is no one rate they jointly
   * state) and the site figure answered in its place: 4,627 of 15,595 sessions
   * printed as 29.68% on a card labelled Engagement Rate, directly beneath one
   * reading Engagements 41,009. Two unrelated populations, presented as a
   * count and its rate. A paid rate falls back to a paid rate or to nothing.
   */
  const er = rateWithGovernance(
    pickFirst(
      /* Co-scoped with the count above — the pair leads only when the count
         actually came from it, so a payload stating its own total never gets
         another population's rate stapled to it. */
      engagementsPair && reportedEngagements === null ? engagementsPair.rate : null,
      t.engagementRate,
      canonicalRates.engagementRate
    ),
    "engagementRate",
    () => computeRatePercent(engagements, impressions)
  );
  const vvr = rateWithGovernance(
    pickFirst(
      t.videoViewRate,
      t.video_view_rate,
      t.viewRate,
      t.view_rate,
      canonicalRates.videoViewRate
    ),
    "vvr",
    () => computeRatePercent(videoViews, impressions)
  );
  const completionRate = rateWithGovernance(
    pickFirst(
      /* Co-scoped with the count above — but only when the count actually came
         FROM the pair. If the payload stated its own count, the pair's rate
         belongs to a population this strip is not showing, and pairing them
         would recreate the mismatch in the other direction. */
      reportedCompletions === null && completionsPair ? completionsPair.rate : null,
      t.videoCompletionRate,
      t.video_completion_rate,
      t.completionRate,
      t.completion_rate,
      canonicalRates.videoCompletionRate ?? canonicalRates.completionRate
    ),
    "completionRate",
    /*
     * Starts-pinned (BE #197). Views used to close this list, so a source
     * that reported views but never starts displayed completions ÷ views as
     * if it were a completion rate. A view is not a start: with no start
     * evidence the rate is unavailable, not approximated.
     */
    () =>
      computeRatePercent(
        videoCompletions,
        pickFirst(t.video_started, t.videoStarted, t.video_starts, t.videoStarts)
      )
  );

  const cpc = rateWithGovernance(
    pickFirst(t.cpc, t.cost_per_click, canonicalRates.cpc),
    "cpc",
    () => (clicks > 0 && spend > 0 && Number.isFinite(spend / clicks) ? spend / clicks : null)
  );
  const cpm = rateWithGovernance(
    pickFirst(t.cpm, t.cost_per_mille, canonicalRates.cpm),
    "cpm",
    () =>
      impressions > 0 && spend > 0 && Number.isFinite((spend / impressions) * 1000)
        ? (spend / impressions) * 1000
        : null
  );

  const conversionValueRaw =
    t.conversion_value_usd !== undefined
      ? t.conversion_value_usd
      : t.conversionValueUsd !== undefined
      ? t.conversionValueUsd
      : t.conversion_value !== undefined
      ? t.conversion_value
      : t.conversionValue;

  const conversionValueNum =
    conversionValueRaw == null || conversionValueRaw === "" ? null : toNumber(conversionValueRaw);
  const conversionValue =
    conversionValueNum != null && Number.isFinite(conversionValueNum) ? conversionValueNum : null;

  const ticketSalesRaw =
    t.ticket_sales_usd ??
    t.ticketSalesUsd ??
    t.ticket_sales ??
    t.ticketSales ??
    t.ticket_sales_total ??
    t.ticketSalesTotal;
  const ticketSalesNum =
    ticketSalesRaw == null || ticketSalesRaw === "" ? null : toNumber(ticketSalesRaw);
  const ticketSales = ticketSalesNum != null && Number.isFinite(ticketSalesNum) ? ticketSalesNum : null;

  return {
    preserveMissingMetrics,
    impressions: Number.isFinite(impressions) ? impressions : unavailableMetric,
    clicks: Number.isFinite(clicks) ? clicks : unavailableMetric,
    engagements: Number.isFinite(engagements) ? engagements : unavailableMetric,
    videoViews: Number.isFinite(videoViews) ? videoViews : unavailableMetric,
    /*
     * NULL, not 0, when the count is withheld or absent. A campaign that
     * reported no completion count did not report a completion count of none,
     * and the two render differently on purpose: "—" against "0".
     */
    videoCompletions: Number.isFinite(videoCompletions) ? videoCompletions : null,
    /* The campaign-level video headline, straight from the governed basis.
       `qualifiedVideo` carries its own contributions for the surfaces that
       show which platforms produced the total. */
    qualifiedVideoViews:
      qualifiedVideoViews !== null
        ? qualifiedVideoViews
        : numberOrNull(governedAggregate?.qualifiedVideo?.value),
    qualifiedVideo: isPlainObject(qualifiedVideoBasis) ? qualifiedVideoBasis : null,
    /* Priced over the qualified population, never over `spend` below. */
    costPerQualifiedVideoView,
    costPerQualifiedVideoViewBasis: isPlainObject(costPerQualifiedVideoViewBasis)
      ? costPerQualifiedVideoViewBasis
      : null,
    spend: Number.isFinite(spend) ? spend : unavailableMetric,
    conversions: Number.isFinite(conversions) ? conversions : unavailableMetric,
    ctr,
    er,
    vvr,
    completionRate,
    cpc,
    cpm,
    conversionValue,
    ticketSales,
    /*
     * How the blended rates above were resolved: the canonical payload's own
     * derivedMetrics, the shared engine over per-channel evidence, or null
     * when only whole-scope totals existed. Surfaces use this to explain a
     * rate's participants instead of asserting one they cannot know.
     */
    rateBasis: governedBases,
    /* And which platforms each count/rate pair describes, for the same
       reason: a count slot that must explain an absence needs the pair's
       governed reason, not a guess. */
    countRatePairs: governedPairs || null,
  };
};

/*
 * What a KPI slot says when the governed basis has no number.
 *
 * A rate can be absent for reasons that are not remotely alike — its sources
 * measure different things, no eligible platform has reported yet, nothing here
 * reports it at all — and "No Data" asserts the least likely of them. The basis
 * already knows which it is, so the slot states it. Payloads that predate the
 * reason field fall back to the historical wording rather than guessing.
 */
const unavailableRateLabel = (metrics, metricKey) => {
  const described = describeUnavailableBasis(metrics?.rateBasis?.bases?.[metricKey]);
  return described?.label || UNGOVERNED_UNAVAILABLE_LABEL;
};

const buildAiOptimizationKpisFromMetrics = (
  metrics,
  base = DEFAULT_AI_OPTIMIZATION_KPIS
) => {
  if (!metrics) return null;

  const completionPair = reconcileCountWithRate({
    count: metrics.videoCompletions,
    rate: metrics.completionRate,
  });

  return {
    ...base,
    adSpend: metrics.preserveMissingMetrics && metrics.spend === null ? "No Data" : formatMoney(metrics.spend),
    impressions: metrics.preserveMissingMetrics && metrics.impressions === null ? "No Data" : formatNumber(metrics.impressions),
    clicks: metrics.preserveMissingMetrics && metrics.clicks === null ? "No Data" : formatNumber(metrics.clicks),
    ctr: metrics.ctr != null ? formatPercent(metrics.ctr) : unavailableRateLabel(metrics, "ctr"),
    cpc: metrics.cpc != null ? formatMoney(metrics.cpc) : unavailableRateLabel(metrics, "cpc"),
    engagements: metrics.preserveMissingMetrics && metrics.engagements === null ? "No Data" : formatNumber(metrics.engagements),
    engagementRate:
      metrics.er != null
        ? formatPercent(metrics.er)
        : unavailableRateLabel(metrics, "engagementRate"),
    /*
     * The campaign-level video headline, from the governed basis. Absent
     * qualified evidence renders the governed unavailable wording — never a
     * zero, and never a bare em dash that leaves the reason unstated.
     */
    qualifiedVideoViews:
      metrics.qualifiedVideoViews != null
        ? formatNumber(metrics.qualifiedVideoViews)
        : UNGOVERNED_UNAVAILABLE_LABEL,
    /*
     * Through `formatCostPerCompletedView`, not a second money formatter. The
     * `unit_cost` rounding is the shared abstraction — the module is named for
     * CPCV but the unit is what is being reused, and declaring a second
     * rounding for the same kind of number is exactly the parity drift the
     * byte-pinned twins exist to prevent.
     */
    costPerQualifiedVideoView:
      metrics.costPerQualifiedVideoView != null
        ? formatCostPerCompletedView(metrics.costPerQualifiedVideoView)
        : /*
           * Explained from its OWN basis, not from `rateBasis.bases`. CPQV is
           * not a METRIC_BASIS_DEFINITION — it is folded by a dedicated
           * aggregator over the qualified population — so the rate-basis
           * lookup finds nothing and every absence would read with the same
           * generic wording. The basis knows whether it declined for a missing
           * denominator or because nothing reported.
           */
          describeUnavailableBasis(metrics.costPerQualifiedVideoViewBasis)?.label ||
          UNGOVERNED_UNAVAILABLE_LABEL,
    videoViews: metrics.preserveMissingMetrics && metrics.videoViews === null ? "No Data" : formatNumber(metrics.videoViews),
    videoViewRate:
      metrics.vvr != null ? formatPercent(metrics.vvr) : unavailableRateLabel(metrics, "vvr"),
    /*
     * The count and its rate are published as a reconciled PAIR.
     *
     * A positive completion rate beside a zero or absent completion count
     * describes a campaign that cannot exist, and a reader cannot tell which
     * half to trust — so the derived half is withheld and the reported half
     * stands. See reconcileCountWithRate in the shared engine.
     */
    videoCompletions:
      completionPair.count != null ? formatNumber(completionPair.count) : UNGOVERNED_UNAVAILABLE_LABEL,
    videoCompletionRate:
      completionPair.rate != null
        ? formatPercent(completionPair.rate)
        : unavailableRateLabel(metrics, "completionRate"),
    conversions: metrics.preserveMissingMetrics && metrics.conversions === null ? "No Data" : formatNumber(metrics.conversions),
    conversionValue:
      metrics.conversionValue != null ? formatMoney(metrics.conversionValue) : "No Data",
  };
};

const buildAiOptimizationKpisFromCanonicalPayload = (
  payload,
  base = DEFAULT_AI_OPTIMIZATION_KPIS
) => {
  const aggregate = findCanonicalAggregateDeep(payload || {});
  const metrics = computeAggregateMetrics({ aggregate });
  return buildAiOptimizationKpisFromMetrics(metrics, base);
};

const fetchLatestAiAggregate = async ({
  advertiserId,
  apiBaseUrl,
  getAuthHeader,
  fetchImpl = fetch,
  signal,
  timeframe = null,
  campaignId = null,
  campaignName = null,
}) => {
  const safeId = String(advertiserId || "").trim();
  if (!safeId) return null;

  const params = new URLSearchParams({
    advertiserId: safeId,
  });
  const start = String(timeframe?.start || timeframe?.date_start || "").trim();
  const end = String(timeframe?.end || timeframe?.date_end || "").trim();
  if (start) params.set("startDate", start);
  if (end) params.set("endDate", end);
  const requestedCampaignId = String(campaignId || timeframe?.campaignId || timeframe?.campaign_id || "").trim();
  const requestedCampaignName = String(campaignName || timeframe?.campaignName || timeframe?.campaign_name || "").trim();
  if (requestedCampaignId && requestedCampaignId !== "auto") {
    params.set("campaignId", requestedCampaignId);
    params.set("campaign_id", requestedCampaignId);
  }
  if (requestedCampaignName) {
    params.set("campaignName", requestedCampaignName);
    params.set("campaign_name", requestedCampaignName);
  }

  const endpoints = [
    `${apiBaseUrl}/api/dashboard/performance-summary?${params.toString()}`,
  ];

  for (const url of endpoints) {
    try {
      const res = await fetchImpl(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...(getAuthHeader?.() || {}) },
        cache: "no-store",
        signal,
      });

      if (!res.ok) continue;

      const payload = await res.json().catch(() => null);
      const canonicalAggregate = findCanonicalAggregateDeep(payload || {});
      if (canonicalAggregate) {
        return {
          aggregate: canonicalAggregate,
          resolved: null,
          payload,
          source: "canonical_performance_summary",
        };
      }
    } catch (err) {
      if (err?.name === "AbortError") throw err;
    }
  }

  return null;
};

const hasAnyAiOptimizationKpiValues = (kpis) => {
  if (!kpis) return false;
  return AI_OPTIMIZATION_KPI_FIELDS.some(([field]) => String(kpis?.[field] ?? "").trim());
};

const normalizeAiOptimizationKpiPayload = (
  value,
  base = DEFAULT_AI_OPTIMIZATION_KPIS
) => {
  if (!isPlainObject(value) || !Object.keys(value).length) return null;
  const normalized = { ...base, ...value };
  return hasAnyAiOptimizationKpiValues(normalized) ? normalized : null;
};

const extractAiOptimizationCurrentKpisFromPayload = (
  payload,
  base = DEFAULT_AI_OPTIMIZATION_KPIS
) => {
  if (!isPlainObject(payload)) return null;

  const explicitCurrentPayload = payload.currentKpis;
  if (
    isPlainObject(explicitCurrentPayload) &&
    (explicitCurrentPayload.aggregateTotals ||
      explicitCurrentPayload.metrics ||
      explicitCurrentPayload.totals)
  ) {
    try {
      const currentKpis = buildAiOptimizationKpisFromCanonicalPayload(
        {
          contract: "canonical_reporting_v1",
          ...explicitCurrentPayload,
          aggregateTotals:
            explicitCurrentPayload.aggregateTotals ||
            explicitCurrentPayload.metrics ||
            explicitCurrentPayload.totals,
        },
        base
      );
      if (hasAnyAiOptimizationKpiValues(currentKpis)) return { ...base, ...currentKpis };
    } catch (err) {
      console.warn("Explicit current KPI aggregate could not be mapped:", err?.message || err);
    }
  }

  try {
    const canonicalPayload =
      payload?.canonicalMetrics?.summary ||
      payload?.canonicalReporting ||
      payload?.canonicalSummary ||
      null;
    const currentKpis = buildAiOptimizationKpisFromCanonicalPayload(canonicalPayload, base);
    if (hasAnyAiOptimizationKpiValues(currentKpis)) return { ...base, ...currentKpis };
  } catch (err) {
    console.warn("Canonical current KPI payload could not be mapped:", err?.message || err);
  }

  const explicitCurrentKpis = normalizeAiOptimizationKpiPayload(explicitCurrentPayload, base);
  if (explicitCurrentKpis) return explicitCurrentKpis;

  if (String(payload?.kpiSourceType || "").trim() === "canonical_current") {
    const directCurrentKpis = normalizeAiOptimizationKpiPayload(payload.kpis, base);
    if (directCurrentKpis) return directCurrentKpis;
  }

  return null;
};

const extractAiOptimizationSavedSnapshotKpisFromPayload = (
  payload,
  base = DEFAULT_AI_OPTIMIZATION_KPIS
) =>
  normalizeAiOptimizationKpiPayload(
    payload?.savedSnapshotKpis ??
      (payload?.kpiSourceType === "saved_snapshot" ? payload?.kpis : null),
    base
  );

const resolveAiOptimizationKpisFromPayloads = ({
  currentPayload = null,
  basePayload = null,
  base = DEFAULT_AI_OPTIMIZATION_KPIS,
} = {}) => {
  const current =
    extractAiOptimizationCurrentKpisFromPayload(currentPayload, base) ||
    extractAiOptimizationCurrentKpisFromPayload(basePayload, base);
  if (current) {
    return {
      kpis: { ...base, ...current },
      hasCurrentKpis: true,
      source: "canonical_current",
    };
  }

  const saved = extractAiOptimizationSavedSnapshotKpisFromPayload(basePayload, base);
  if (saved) {
    return {
      kpis: { ...base, ...saved },
      hasCurrentKpis: false,
      source: "saved_snapshot_fallback",
    };
  }

  return {
    kpis: { ...base },
    hasCurrentKpis: false,
    source: null,
  };
};

const readSherlocLastRunSnapshot = (advertiserId) => {
  const safeId = String(advertiserId || "").trim();
  if (!safeId) return null;

  const keys = [`clSherlocLastRun:v2:${safeId}`, `clSherlocLastRun:${safeId}`];

  try {
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      return {
        aggregate: normalizeAggregateLike(parsed?.aggregate) || null,
        resolved: normalizeResolvedLike(parsed?.resolved) || null,
        payload: parsed || null,
      };
    }
  } catch {
    return null;
  }

  return null;
};

/*
 * The co-scoped count/rate pair, read from the governed payload.
 *
 * Exported because more than one surface needs to answer "may this count be
 * printed beside this rate, and about whom?" and the answer must not be
 * re-derived per page. `countRatePairs` states the count on the RATE's own
 * population — a completion count scoped to the platforms that also reported
 * starts, an engagement count scoped to the platform that declared the
 * measurement — so a consumer reading both halves from one pair cannot staple
 * a rate to a count measured about someone else.
 *
 * Null when the pair is absent or carries no count, which is a different
 * statement from zero and must stay distinguishable.
 */
export const selectGovernedCountRatePair = (governedAggregate, pairKey) => {
  const pairs = isPlainObject(governedAggregate?.countRatePairs)
    ? governedAggregate.countRatePairs
    : null;
  const pair = pairs?.[pairKey];
  return isPlainObject(pair) && pair.count !== null && pair.count !== undefined ? pair : null;
};

export {
  AI_OPTIMIZATION_KPI_FIELDS,
  DEFAULT_AI_OPTIMIZATION_KPIS,
  buildAiOptimizationKpisFromMetrics,
  buildAiOptimizationKpisFromCanonicalPayload,
  computeAggregateMetrics,
  extractAiOptimizationCurrentKpisFromPayload,
  extractAiOptimizationSavedSnapshotKpisFromPayload,
  fetchLatestAiAggregate,
  findAggregateDeep,
  findCanonicalAggregateDeep,
  findResolvedDeep,
  hasAnyAiOptimizationKpiValues,
  resolveAiOptimizationKpisFromPayloads,
  readSherlocLastRunSnapshot,
};
